import { useState, useEffect } from "react";
import {
  FaArrowLeft,
  FaPhone,
  FaEnvelope,
  FaWifi,
  FaMoneyBillWave,
  FaArrowTrendUp,
  FaArrowTrendDown,
  FaReceipt,
  FaChartLine,
} from "react-icons/fa6";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { FaCalendarAlt, FaMapMarkerAlt, FaUserCircle } from "react-icons/fa";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/* ============================================
   Dark Theme Chart.js Defaults
   ============================================ */
ChartJS.defaults.color = "#64748B";
ChartJS.defaults.borderColor = "rgba(51, 65, 85, 0.2)";

const TOOLTIP_STYLE = {
  backgroundColor: "rgba(11, 20, 38, 0.95)",
  titleColor: "#F0F4F8",
  bodyColor: "#94A3B8",
  borderColor: "rgba(0, 180, 216, 0.2)",
  borderWidth: 1,
  padding: 14,
  cornerRadius: 10,
  displayColors: true,
  boxPadding: 6,
  titleFont: { size: 13, weight: "600" },
  bodyFont: { size: 12 },
  usePointStyle: true,
};

/* ============================================
   Dummy Data Reseller
   ============================================ */
const DUMMY_RESELLER = {
  id: "res-001",
  name: "Ahmad Fauzi",
  email: "ahmad.fauzi@hotspot.id",
  phone_number: "0812-3456-7890",
  address: "Jl. Raya Indah No. 15, Jakarta Selatan",
  status: "Aktif",
  join_date: "2023-01-10",
  vouchers: {
    total_sold: 1250,
    total_revenue: 6250000,
    active_vouchers: 45,
  },
  sales_chart: {
    labels: ["Jul", "Agu", "Sep", "Okt", "Nov", "Des"],
    data: [180, 220, 195, 240, 210, 305], // Jumlah voucher terbul per bulan
  },
  stor_history: [
    { id: "st-1", date: "2023-12-01", voucher: "Voucher 3 Jam", qty: 50, total: 250000, status: "Lunas" },
    { id: "st-2", date: "2023-12-05", voucher: "Voucher 1 Hari", qty: 30, total: 300000, status: "Lunas" },
    { id: "st-3", date: "2023-12-10", voucher: "Voucher 1 Minggu", qty: 10, total: 200000, status: "Belum Lunas" },
    { id: "st-4", date: "2023-12-15", voucher: "Voucher 3 Jam", qty: 80, total: 400000, status: "Lunas" },
  ],
  financials: {
    total_piutang: 200000,
    total_hutang: 0,
    history: [
      { id: "f-1", date: "2023-12-10", type: "Piutang", desc: "Penjualan Voucher 1 Minggu (Stor tertunda)", amount: 200000 },
      { id: "f-2", date: "2023-11-20", type: "Lunas", desc: "Pembayaran Piutang Bulan Lalu", amount: 150000 },
    ],
  },
};

/* ============================================
   Sub-Komponen: SectionCard
   ============================================ */
const SectionCard = ({ title, icon: Icon, children }) => (
  <article
    className="rounded-2xl p-5 sm:p-6 transition-all duration-300"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
      backdropFilter: "blur(12px)",
    }}
  >
    <div className="flex items-center gap-3 mb-5">
      {Icon && (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{
            background: "rgba(0, 180, 216, 0.1)",
            border: "1px solid rgba(0, 180, 216, 0.2)",
          }}
        >
          <Icon className="text-ocean-400 text-sm" />
        </div>
      )}
      <h3 className="font-display text-base sm:text-lg font-bold text-gray-100">
        {title}
      </h3>
    </div>
    {children}
  </article>
);

/* ============================================
   Sub-Komponen: StatusBadge
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "Aktif" || status === "Lunas";
  const isPending = status === "Belum Lunas";
  
  let styleClass = "bg-slate-500/12 text-slate-400 border-slate-500/20";
  if (isActive) styleClass = "bg-emerald-500/12 text-emerald-400 border-emerald-500/20";
  if (isPending) styleClass = "bg-amber-500/12 text-amber-400 border-amber-500/20";

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${styleClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : isPending ? "bg-amber-400" : "bg-slate-400"}`} />
      {status}
    </span>
  );
};

/* ============================================
   Format Currency
   ============================================ */
const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/* ============================================
   Halaman Utama: ResellerDetail
   ============================================ */
export const ResellerDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showSidebar, setShowSidebar] = useState(false);
  const [reseller, setReseller] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulasi fetch API berdasarkan ID
    setTimeout(() => {
      setReseller(DUMMY_RESELLER);
      setIsLoading(false);
    }, 800);
  }, [id]);

  if (isLoading || !reseller) {
    return (
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <div className="p-6 flex items-center justify-center h-screen">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full border-4 border-slate-600 border-t-ocean-400 animate-spin" />
            <p className="text-slate-400">Memuat data reseller...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  /* ---------- Chart Config ---------- */
  const chartData = {
    labels: reseller.sales_chart.labels,
    datasets: [
      {
        label: "Voucher Terjual",
        data: reseller.sales_chart.data,
        backgroundColor: "rgba(0, 180, 216, 0.6)",
        hoverBackgroundColor: "rgba(0, 180, 216, 0.8)",
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 32,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...TOOLTIP_STYLE,
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y} Voucher`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#64748B", font: { size: 12, weight: "500" } },
      },
      y: {
        grid: { color: "rgba(51, 65, 85, 0.2)", drawBorder: false },
        ticks: { color: "#64748B", font: { size: 11, weight: "500" } },
        beginAtZero: true,
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Detail Reseller {reseller.name} - DANELA NET</title>
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
            >
              <FaArrowLeft />
            </button>
            <div className="flex flex-col">
              <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
                Detail Reseller
              </h2>
              <p className="text-xs text-slate-500 hidden md:block mt-0.5">
                Informasi penjualan dan riwayat transaksi
              </p>
            </div>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6">
          
          {/* Row 1: Profile & Hutang/Piutang */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Card Profil Utama */}
            <div className="lg:col-span-1">
              <SectionCard title="Profil Reseller" icon={FaUserCircle}>
                <div className="flex flex-col items-center text-center">
                  <div
                    className="w-24 h-24 rounded-2xl flex items-center justify-center font-bold text-3xl flex-shrink-0 mb-4"
                    style={{
                      background: "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                      border: "1px solid rgba(0, 180, 216, 0.15)",
                      color: "#48CAE4",
                    }}
                  >
                    {reseller.name?.charAt(0).toUpperCase()}
                  </div>
                  
                  <h2 className="text-xl font-bold text-gray-100">{reseller.name}</h2>
                  <div className="mt-2 mb-4">
                    <StatusBadge status={reseller.status} />
                  </div>

                  <div className="w-full space-y-3 text-left mt-2">
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaPhone className="text-ocean-400 w-5" />
                      <span className="text-slate-300">{reseller.phone_number}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaEnvelope className="text-ocean-400 w-5" />
                      <span className="text-slate-300">{reseller.email}</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm text-slate-400">
                      <FaMapMarkerAlt className="text-ocean-400 w-5 mt-0.5" />
                      <span className="text-slate-300">{reseller.address}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaCalendarAlt className="text-ocean-400 w-5" />
                      <span className="text-slate-300">Bergabung: {reseller.join_date}</span>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Hutang & Piutang */}
            <div className="lg:col-span-2">
              <SectionCard title="Data Hutang & Piutang" icon={FaMoneyBillWave}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {/* Piutang */}
                  <div className="p-4 rounded-xl" style={{ background: "rgba(16, 185, 129, 0.05)", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Total Piutang</span>
                      <FaArrowTrendUp className="text-emerald-400" />
                    </div>
                    <p className="text-2xl font-bold text-emerald-400">{formatCurrency(reseller.financials.total_piutang)}</p>
                  </div>
                  {/* Hutang */}
                  <div className="p-4 rounded-xl" style={{ background: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Total Hutang</span>
                      <FaArrowTrendDown className="text-red-400" />
                    </div>
                    <p className="text-2xl font-bold text-red-400">{formatCurrency(reseller.financials.total_hutang)}</p>
                  </div>
                </div>

                {/* Tabel Riwayat Hutang/Piutang */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700/50">
                        <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tanggal</th>
                        <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipe</th>
                        <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Keterangan</th>
                        <th className="pb-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Jumlah</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/30">
                      {reseller.financials.history.map((item) => (
                        <tr key={item.id}>
                          <td className="py-3 text-slate-400 whitespace-nowrap">{item.date}</td>
                          <td className="py-3">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${item.type === 'Piutang' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : item.type === 'Hutang' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'}`}>
                              {item.type}
                            </span>
                          </td>
                          <td className="py-3 text-slate-300 font-medium">{item.desc}</td>
                          <td className={`py-3 text-right font-semibold whitespace-nowrap ${item.type === 'Piutang' ? 'text-emerald-400' : item.type === 'Hutang' ? 'text-red-400' : 'text-slate-400'}`}>
                            {item.type === 'Lunas' ? '+' : item.type === 'Piutang' ? '+' : '-'} {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </div>
          </div>

          {/* Row 2: Statistik Voucher & Grafik Penjualan */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Stats & Grafik */}
            <div className="lg:col-span-2 space-y-6">
              {/* Voucher Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-2xl p-5 transition-all duration-300" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                      <FaReceipt />
                    </div>
                    <span className="text-xs font-semibold text-slate-400 uppercase">Total Terjual</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-100">{reseller.vouchers.total_sold.toLocaleString('id-ID')}</p>
                  <p className="text-xs text-slate-500 mt-1">Voucher</p>
                </div>
                <div className="rounded-2xl p-5 transition-all duration-300" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      <FaMoneyBillWave />
                    </div>
                    <span className="text-xs font-semibold text-slate-400 uppercase">Total Pendapatan</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-100">{formatCurrency(reseller.vouchers.total_revenue)}</p>
                  <p className="text-xs text-slate-500 mt-1">Kumulatif</p>
                </div>
                <div className="rounded-2xl p-5 transition-all duration-300" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                      <FaWifi />
                    </div>
                    <span className="text-xs font-semibold text-slate-400 uppercase">Aktif Sekarang</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-100">{reseller.vouchers.active_vouchers}</p>
                  <p className="text-xs text-slate-500 mt-1">Sedang digunakan</p>
                </div>
              </div>

              {/* Chart */}
              <SectionCard title="Grafik Penjualan Voucher" icon={FaChartLine}>
                <div className="h-[300px]">
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </SectionCard>
            </div>

            {/* Stor Hasil Penjualan */}
            <div className="lg:col-span-1">
              <SectionCard title="Stor Penjualan" icon={FaReceipt}>
                <div className="space-y-3 max-h-[450px] overflow-y-auto pr-1">
                  {reseller.stor_history.map((item) => (
                    <div 
                      key={item.id} 
                      className="p-3 rounded-xl"
                      style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-sm font-semibold text-gray-100">{item.voucher}</p>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">{item.date}</p>
                        </div>
                        <StatusBadge status={item.status} />
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-slate-700/30">
                        <span className="text-xs text-slate-400">Qty: {item.qty}</span>
                        <span className="text-sm font-bold text-ocean-400">{formatCurrency(item.total)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          </div>

        </main>
      </DashboardLayout>
    </>
  );
};